// DocFilePreview — in-browser preview of a stored Docs Hub file, shared by the
// Documents library pane and chat file drafts. PDF bytes render in a native
// iframe; docx/xlsx/csv render as BFF HTML in a sandboxed iframe. Without a
// revision id it shows the current revision (through the BFF download, so
// imported rows without sidecar bytes still load).

import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, FileText, Loader2 } from 'lucide-react'
import {
  docRevisionObjectUrl,
  libraryDocObjectUrl,
  useGetDocPreviewQuery
} from '@axilon-apps/domains-ontology-ui-state'

import { pdfPreviewHref } from '../../lib/document-preview'
import { previewKind } from './doc-helpers'

/** Base stylesheet injected into the HTML-preview iframe (paper-like). */
const PREVIEW_CSS = `
  :root { color-scheme: light; }
  body {
    margin: 0;
    padding: 32px 40px;
    background: #fff;
    color: #1a1a1a;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.55;
  }
  h1, h2, h3 { line-height: 1.25; }
  table { border-collapse: collapse; margin: 12px 0; max-width: 100%; }
  td, th { border: 1px solid #d4d4d8; padding: 4px 8px; vertical-align: top; }
  section + section { margin-top: 28px; }
  section > h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .05em; color: #71717a; }
  img { max-width: 100%; height: auto; }
  p.truncated, p.empty { color: #a1a1aa; font-style: italic; font-size: 12px; }
`

function wrapHtml(fragment: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>${PREVIEW_CSS}</style></head><body>${fragment}</body></html>`
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: { message?: unknown } }).data
    if (data && typeof data.message === 'string') return data.message
  }
  return fallback
}

const CenteredState = ({
  icon,
  children
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) => (
  <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
    {icon}
    <p className="m-0 max-w-md text-sm text-ink-3">{children}</p>
  </div>
)

interface BodyProps {
  docId: string
  revisionId?: string
  name: string
}

const PdfBody = ({ docId, revisionId, name, sourcePage }: BodyProps & { sourcePage?: number }) => {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let revoked = false
    let objectUrl: string | null = null
    setUrl(null)
    setError(null)
    const load = revisionId ? docRevisionObjectUrl(docId, revisionId) : libraryDocObjectUrl(docId)
    load
      .then((next) => {
        if (revoked) {
          URL.revokeObjectURL(next)
          return
        }
        objectUrl = next
        setUrl(next)
      })
      .catch((err: unknown) => {
        if (!revoked) setError(err instanceof Error ? err.message : 'Failed to load PDF')
      })
    return () => {
      revoked = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [docId, revisionId])

  if (error) {
    return (
      <CenteredState icon={<AlertCircle className="size-8 text-st-down" aria-hidden />}>
        {error}
      </CenteredState>
    )
  }
  if (!url) {
    return (
      <CenteredState icon={<Loader2 className="size-6 animate-spin text-ink-3" aria-hidden />}>
        Loading preview…
      </CenteredState>
    )
  }
  return (
    <iframe
      src={pdfPreviewHref(url, sourcePage)}
      title={`${name} preview`}
      className="size-full border-0"
    />
  )
}

const HtmlBody = ({ docId, revisionId, name }: BodyProps) => {
  const preview = useGetDocPreviewQuery({ id: docId, ...(revisionId ? { revisionId } : {}) })
  const srcDoc = useMemo(() => (preview.data ? wrapHtml(preview.data.html) : null), [preview.data])

  if (preview.isLoading) {
    return (
      <CenteredState icon={<Loader2 className="size-6 animate-spin text-ink-3" aria-hidden />}>
        Rendering preview…
      </CenteredState>
    )
  }
  if (preview.isError || srcDoc === null) {
    return (
      <CenteredState icon={<AlertCircle className="size-8 text-st-down" aria-hidden />}>
        {errorMessage(preview.error, 'Could not render this document. Try downloading it instead.')}
      </CenteredState>
    )
  }
  return (
    <iframe
      sandbox=""
      srcDoc={srcDoc}
      title={`${name} preview`}
      className="size-full border-0 bg-white"
    />
  )
}

export interface DocFilePreviewProps {
  docId: string
  /** Revision to show; the document's current revision when omitted. */
  revisionId?: string
  name: string
  format: string
  sourcePage?: number
}

export default function DocFilePreview({
  docId,
  revisionId,
  name,
  format,
  sourcePage
}: DocFilePreviewProps) {
  const kind = previewKind(format)
  if (kind === 'pdf') {
    return <PdfBody docId={docId} revisionId={revisionId} name={name} sourcePage={sourcePage} />
  }
  if (kind === 'html') return <HtmlBody docId={docId} revisionId={revisionId} name={name} />
  return (
    <CenteredState icon={<FileText className="size-8 text-ink-3" aria-hidden />}>
      No in-browser preview for “.{format}” files. Download it to view.
    </CenteredState>
  )
}
